<template>
  <div class="d3-chart-wrapper" style="position: relative; margin-top: 20px;">
    <div ref="chartContainer" class="chart-container"></div>
    <!-- Custom Tooltip -->
    <div ref="tooltip" class="d3-tooltip" style="opacity: 0; pointer-events: none; position: absolute; background: rgba(0, 0, 0, 0.8); color: #fff; padding: 10px; border-radius: 4px; font-size: 12px; z-index: 10; box-shadow: 0 4px 6px rgba(0,0,0,0.3);"></div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import { formatDateTime } from '@/util/date';
import { CHART_AXIS, CHART_TOOLTIP } from '@/util/apiDatetime';
import { interpolateNulls } from '@/util/summary/series';

export default {
  name: 'BunkerSummaryChart',
  props: {
    mappedSeries: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      resizeObserver: null,
      seriesVisibility: {},
    };
  },
  mounted() {
    this.drawChart();
    this.resizeObserver = new ResizeObserver(() => this.drawChart());
    this.resizeObserver.observe(this.$el);
  },
  beforeDestroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  },
  watch: {
    mappedSeries: {
      deep: true,
      handler() {
        this.drawChart();
      },
    },
  },
  methods: {

    processData() {
      if (!this.mappedSeries || this.mappedSeries.length === 0) return null;

      const getSafeData = (data, key) => {
        const raw = data?.fmData?.BUNKER_IN?.[key];
        if (raw !== null && typeof raw !== 'undefined' && Number.isFinite(Number(raw))) {
          return Number(Number(raw).toFixed(2));
        }
        return null;
      };

      const MassTotal = this.mappedSeries.map((d) => getSafeData(d, 'massTotal'));
      const MassFlow = this.mappedSeries.map((d) => getSafeData(d, 'massFlowrate'));
      const VolTotal = this.mappedSeries.map((d) => getSafeData(d, 'volumeTotal'));
      const VolFlow = this.mappedSeries.map((d) => getSafeData(d, 'volumeFlowrate'));
      const Density = this.mappedSeries.map((d) => getSafeData(d, 'density'));
      const Temp = this.mappedSeries.map((d) => getSafeData(d, 'temperature'));

      const timestamps = this.mappedSeries.map((d) => new Date(d.timestamp * 1000));

      const datasets = [
        {
          name: 'Mass Total', data: MassTotal, interpolated: interpolateNulls(MassTotal), color: '#3B82F6', axis: 'massTotal',
        },
        {
          name: 'Mass Flow', data: MassFlow, interpolated: interpolateNulls(MassFlow), color: '#06B6D4', axis: 'massFlow',
        },
        {
          name: 'Vol Total', data: VolTotal, interpolated: interpolateNulls(VolTotal), color: '#8B5CF6', axis: 'volTotal',
        },
        {
          name: 'Vol Flow', data: VolFlow, interpolated: interpolateNulls(VolFlow), color: '#EC4899', axis: 'volFlow',
        },
        {
          name: 'Density', data: Density, interpolated: interpolateNulls(Density), color: '#10B981', axis: 'density',
        },
        {
          name: 'Temp (°C)', data: Temp, interpolated: interpolateNulls(Temp), color: '#F97316', axis: 'temp',
        },
      ];

      return { timestamps, datasets };
    },

    drawChart() {
      const processed = this.processData();
      if (!processed) return;
      const { timestamps, datasets } = processed;
      const container = this.$refs.chartContainer;

      d3.select(container).selectAll('*').remove();

      const visibility = this.seriesVisibility || {};
      datasets.forEach((dataset) => {
        if (visibility[dataset.name] === undefined) visibility[dataset.name] = true;
      });
      this.seriesVisibility = visibility;

      const visibleWidth = this.$el.clientWidth || container.clientWidth;
      const isCompact = visibleWidth < 576;
      const chartOuterWidth = Math.max(container.clientWidth, isCompact ? 720 : 0);

      const margin = {
        top: 30,
        right: isCompact ? 160 : 200,
        bottom: isCompact ? 48 : 50,
        left: isCompact ? 100 : 120,
      };
      const width = Math.max(1, chartOuterWidth - margin.left - margin.right);
      const chartHeight = isCompact ? 420 : 600;
      const height = chartHeight - margin.top - margin.bottom;
      const rightAxisOffset = isCompact ? 40 : 50;
      const leftAxisOffset = isCompact ? 50 : 60;
      const xTickCount = isCompact ? 5 : 8;
      const yTickCount = isCompact ? 5 : 10;

      const svg = d3.select(container)
        .append('svg')
        .attr('width', chartOuterWidth)
        .attr('height', height + margin.top + margin.bottom)
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      // X Axis
      const x = d3.scaleTime().domain(d3.extent(timestamps)).range([0, width]);
      svg.append('g')
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x).ticks(xTickCount).tickFormat((d) => formatDateTime(d, CHART_AXIS, null, false)))
        .selectAll('text')
        .style('text-anchor', 'end')
        .attr('dx', '-.8em')
        .attr('dy', '.15em')
        .attr('transform', 'rotate(-45)');

      // -- Y Axis Scales Setup --

      // Helper untuk membuat scale linear
      const createScale = (ds) => {
        const maxVal = d3.max(ds.interpolated) || 10;
        return d3.scaleLinear().domain([0, maxVal * 1.1]).range([height, 0]);
      };

      const yMassTotal = createScale(datasets[0]);
      const yMassFlow = createScale(datasets[1]);
      const yVolTotal = createScale(datasets[2]);
      const yVolFlow = createScale(datasets[3]);
      const yDensity = createScale(datasets[4]);
      const yTemp = createScale(datasets[5]);

      // -- Drawing Axes --

      // Left 1: Mass Total (Main Left)
      svg.append('g')
        .call(d3.axisLeft(yMassTotal).ticks(yTickCount))
        .append('text').attr('fill', datasets[0].color)
        .attr('transform', 'rotate(-90)')
        .attr('y', isCompact ? -34 : -40)
        .attr('dy', '0.71em')
        .style('text-anchor', 'end')
        .text(datasets[0].name);

      // Left 2: Mass Flow (Offset Left)
      svg.append('g')
        .attr('transform', `translate(-${leftAxisOffset}, 0)`)
        .call(d3.axisLeft(yMassFlow).ticks(yTickCount))
        .append('text')
        .attr('fill', datasets[1].color)
        .attr('transform', 'rotate(-90)')
        .attr('y', -40)
        .attr('dy', '0.71em')
        .style('text-anchor', 'end')
        .text(datasets[1].name);

      // Right 1: Volume Total (Main Right)
      svg.append('g')
        .attr('transform', `translate(${width}, 0)`)
        .call(d3.axisRight(yVolTotal).ticks(yTickCount))
        .append('text')
        .attr('fill', datasets[2].color)
        .attr('transform', 'rotate(-90)')
        .attr('y', 30)
        .attr('dy', '0.71em')
        .style('text-anchor', 'end')
        .text(datasets[2].name);

      // Right 2: Volume Flow (Offset Right 1)
      svg.append('g')
        .attr('transform', `translate(${width + rightAxisOffset}, 0)`)
        .call(d3.axisRight(yVolFlow).ticks(yTickCount))
        .append('text')
        .attr('fill', datasets[3].color)
        .attr('transform', 'rotate(-90)')
        .attr('y', 30)
        .attr('dy', '0.71em')
        .style('text-anchor', 'end')
        .text(datasets[3].name);

      // Right 3: Density (Offset Right 2)
      svg.append('g')
        .attr('transform', `translate(${width + rightAxisOffset * 2}, 0)`)
        .call(d3.axisRight(yDensity).ticks(yTickCount))
        .append('text')
        .attr('fill', datasets[4].color)
        .attr('transform', 'rotate(-90)')
        .attr('y', 30)
        .attr('dy', '0.71em')
        .style('text-anchor', 'end')
        .text(datasets[4].name);

      // Right 4: Temperature (Offset Right 3)
      svg.append('g')
        .attr('transform', `translate(${width + rightAxisOffset * 3}, 0)`)
        .call(d3.axisRight(yTemp).ticks(yTickCount))
        .append('text')
        .attr('fill', datasets[5].color)
        .attr('transform', 'rotate(-90)')
        .attr('y', 30)
        .attr('dy', '0.71em')
        .style('text-anchor', 'end')
        .text(datasets[5].name);

      // Grid (Based on Mass Total)
      svg.append('g').attr('class', 'grid').attr('opacity', 0.1).call(d3.axisLeft(yMassTotal).ticks(yTickCount).tickSize(-width).tickFormat(''));

      const getScale = (axisName) => {
        switch (axisName) {
          case 'massTotal': return yMassTotal;
          case 'massFlow': return yMassFlow;
          case 'volTotal': return yVolTotal;
          case 'volFlow': return yVolFlow;
          case 'density': return yDensity;
          case 'temp': return yTemp;
          default: return yMassTotal;
        }
      };

      // -- Draw Lines & Dots --
      const seriesElements = {};
      const realLineWidth = 2.5;
      const hoverLineWidth = 4;
      const interpolatedLineWidth = 1.5;
      datasets.forEach((series, index) => {
        const yScale = getScale(series.axis);

        // Interpolated Line (Dashed)
        const interpolatedLine = svg.append('path')
          .datum(series.interpolated)
          .attr('fill', 'none')
          .attr('stroke', series.color)
          .attr('stroke-width', interpolatedLineWidth)
          .attr('stroke-dasharray', '4 4')
          .attr('opacity', 0.6)
          // null di ujung deret tetap null setelah interpolasi; tanpa defined jadi NaN di path
          .attr('d', d3.line().defined((d) => d !== null).x((d, i) => x(timestamps[i])).y((d) => yScale(d)));

        // Real Data Line (Solid)
        const realLine = svg.append('path')
          .datum(series.data)
          .attr('fill', 'none')
          .attr('stroke', series.color)
          .attr('stroke-width', realLineWidth)
          .attr('d', d3.line().defined((d) => d !== null).x((d, i) => x(timestamps[i])).y((d) => yScale(d)))
          .on('mouseover', function handleMouseOver() {
            d3.select(this).attr('stroke-width', hoverLineWidth);
          })
          .on('mouseout', function handleMouseOut() {
            d3.select(this).attr('stroke-width', realLineWidth);
          });

        // Dots for Real Data
        const dots = svg.selectAll(`.dot-series-${index}`)
          .data(series.data.map((d, i) => ({ value: d, date: timestamps[i] })).filter((d) => d.value !== null))
          .enter()
          .append('circle')
          .attr('class', `dot-series-${index}`)
          .attr('cx', (d) => x(d.date))
          .attr('cy', (d) => yScale(d.value))
          .attr('r', 3)
          .attr('fill', series.color)
          .style('pointer-events', 'none');

        seriesElements[series.name] = {
          interpolatedLine,
          realLine,
          dots,
        };
      });

      // -- Interaction (Tooltip & Focus) --
      const tooltip = d3.select(this.$refs.tooltip);
      const bisect = d3.bisector((d) => d).left;

      const focusGroup = svg.append('g').style('display', 'none');
      const focusLine = focusGroup.append('line')
        .attr('stroke', '#aaa').attr('stroke-dasharray', '3 3').attr('y1', 0)
        .attr('y2', height);

      const focusCircles = datasets.map((ds) => focusGroup.append('circle').attr('r', 5).attr('fill', ds.color).attr('stroke', '#fff')
        .attr('stroke-width', 2));

      const updateSeriesVisibility = () => {
        datasets.forEach((ds, index) => {
          const isVisible = visibility[ds.name];
          const elements = seriesElements[ds.name];
          elements.interpolatedLine.style('display', isVisible ? null : 'none');
          elements.realLine.style('display', isVisible ? null : 'none');
          elements.dots.style('display', isVisible ? null : 'none');
          focusCircles[index].style('display', isVisible ? null : 'none');
        });
      };

      const legendSpacing = 18;
      const legendItemWidth = 160;
      const legendColumns = Math.max(1, Math.floor(width / legendItemWidth));
      const legendWidth = Math.min(datasets.length, legendColumns) * legendItemWidth;
      const legendGroup = svg.append('g')
        .attr('class', 'chart-legend')
        .attr('transform', `translate(${Math.max(0, (width - legendWidth) / 2)}, -10)`);
      const legendItem = legendGroup.selectAll('g')
        .data(datasets)
        .enter()
        .append('g')
        .attr('transform', (d, i) => {
          const col = i % legendColumns;
          const row = Math.floor(i / legendColumns);
          return `translate(${col * legendItemWidth}, ${row * legendSpacing})`;
        })
        .style('cursor', 'pointer')
        .on('click', (event, d) => {
          visibility[d.name] = !visibility[d.name];
          updateSeriesVisibility();
          // eslint-disable-next-line no-use-before-define
          updateLegend();
        });

      legendItem.append('rect')
        .attr('width', 12)
        .attr('height', 12)
        .attr('y', -10)
        .attr('rx', 2)
        .attr('ry', 2)
        .attr('fill', (d) => d.color);

      legendItem.append('text')
        .attr('x', 18)
        .attr('y', 0)
        .attr('dy', '0.32em')
        .style('font-size', '12px')
        .text((d) => d.name);

      const updateLegend = () => {
        legendItem.select('rect').attr('opacity', (d) => (visibility[d.name] ? 1 : 0.3));
        legendItem.select('text')
          .attr('opacity', (d) => (visibility[d.name] ? 1 : 0.4))
          .style('text-decoration', (d) => (visibility[d.name] ? 'none' : 'line-through'));
      };

      updateSeriesVisibility();
      updateLegend();

      svg.append('rect')
        .attr('width', width).attr('height', height)
        .style('fill', 'none')
        .style('pointer-events', 'all')
        .on('mouseover', () => { tooltip.style('opacity', 1); focusGroup.style('display', null); })
        .on('mouseout', () => { tooltip.style('opacity', 0); focusGroup.style('display', 'none'); })
        .on('mousemove', (event) => {
          const x0 = x.invert(d3.pointer(event)[0]);
          const i = bisect(timestamps, x0, 1);
          // eslint-disable-next-line no-nested-ternary
          const index = i < timestamps.length && i > 0 ? (x0 - timestamps[i - 1] > timestamps[i] - x0 ? i : i - 1) : 0;
          const date = timestamps[index];
          const xPos = x(date);

          focusLine.attr('x1', xPos).attr('x2', xPos);

          datasets.forEach((ds, i) => {
            if (!visibility[ds.name]) {
              focusCircles[i].style('display', 'none');
              return;
            }
            const val = ds.data[index];
            const yScale = getScale(ds.axis);
            if (val !== null && val !== undefined) {
              focusCircles[i].attr('cx', xPos).attr('cy', yScale(val)).style('display', null);
            } else {
              focusCircles[i].style('display', 'none');
            }
          });

          let html = `<strong>${formatDateTime(date, CHART_TOOLTIP)}</strong><br/>`;
          datasets.forEach((ds) => {
            if (!visibility[ds.name]) return;
            const val = ds.data[index];
            if (val !== null && val !== undefined) {
              html += `<div style="display:flex; align-items:center; gap:5px; margin-top:2px;">
                          <span style="width:10px; height:10px; border-radius:50%; background:${ds.color}; display:inline-block;"></span>
                          ${ds.name}: <strong>${val}</strong>
                        </div>`;
            }
          });

          const [posX, posY] = d3.pointer(event, container);
          let left = posX + 15;
          const top = posY + 15;
          // Prevent tooltip off-screen right
          if (left + 200 > chartOuterWidth) left = posX - 210;

          tooltip.html(html).style('left', `${left}px`).style('top', `${top}px`);
        });
    },
  },
};
</script>

<style scoped>
.d3-chart-wrapper { font-family: inherit; background: #fff; }
/* d3-axis nempelin font-family="sans-serif" di grup axis; CSS menang atas atribut itu. */
.d3-chart-wrapper ::v-deep [font-family] { font-family: inherit; }
.chart-container { width: 100%; }

@media screen and (max-width: 575px) {
  .d3-chart-wrapper {
    min-width: 720px;
  }
}
</style>
